import { useEffect, useRef, useState } from "react";
import AccountButton from "../AccountButton";
import AccountInput from "../AccountInput";
import AccountSection from "../AccountSection";
import {
  addDoctor,
  deleteDoctor,
  getDoctors,
  updateDoctor,
} from "../api/apiFunctions";
import { DisplayOnCardPill } from "../components/Pills";
import { t } from "../i18n";
import { getAuthToken } from "../utils/auth";
import { replaceNullWithPlaceholder } from "../utils";

export default function FindDoctorPage({ lang = "en", goToTab }) {
  const [search, setSearch] = useState("");
  const [stateSearch, setStateSearch] = useState("");
  const [citySearch, setCitySearch] = useState("");
  const [selectedDoctor, setSelectedDoctor] = useState({
    name: "",
    npi: "",
    credential: "",
    specialty: "",
    gender: "",
    salId: "",
    service: "",
    address: "",
    phone: "",
    email: "",
  });
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  // Status filter removed since we now display current and past doctors separately
  const [apiResults, setApiResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isDoctorLoading, setIsDoctorLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Modal states
  const [showAddDoctorModal, setShowAddDoctorModal] = useState(false);
  const [doctorToAdd, setDoctorToAdd] = useState(null);
  const [addDoctorStatus, setAddDoctorStatus] = useState("current");

  // Edit modal states
  const [showEditDoctorModal, setShowEditDoctorModal] = useState(false);
  const [doctorToEdit, setDoctorToEdit] = useState(null);
  const [editDoctorData, setEditDoctorData] = useState({
    name: "",
    role: "",
    specialty: "",
    status: "current",
  });

  // Delete confirmation modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [doctorToDelete, setDoctorToDelete] = useState(null);

  // US States for dropdown
  const usStates = [
    { value: "", label: "Select State" },
    { value: "AL", label: "Alabama" },
    { value: "AK", label: "Alaska" },
    { value: "AZ", label: "Arizona" },
    { value: "AR", label: "Arkansas" },
    { value: "CA", label: "California" },
    { value: "CO", label: "Colorado" },
    { value: "CT", label: "Connecticut" },
    { value: "DE", label: "Delaware" },
    { value: "FL", label: "Florida" },
    { value: "GA", label: "Georgia" },
    { value: "HI", label: "Hawaii" },
    { value: "ID", label: "Idaho" },
    { value: "IL", label: "Illinois" },
    { value: "IN", label: "Indiana" },
    { value: "IA", label: "Iowa" },
    { value: "KS", label: "Kansas" },
    { value: "KY", label: "Kentucky" },
    { value: "LA", label: "Louisiana" },
    { value: "ME", label: "Maine" },
    { value: "MD", label: "Maryland" },
    { value: "MA", label: "Massachusetts" },
    { value: "MI", label: "Michigan" },
    { value: "MN", label: "Minnesota" },
    { value: "MS", label: "Mississippi" },
    { value: "MO", label: "Missouri" },
    { value: "MT", label: "Montana" },
    { value: "NE", label: "Nebraska" },
    { value: "NV", label: "Nevada" },
    { value: "NH", label: "New Hampshire" },
    { value: "NJ", label: "New Jersey" },
    { value: "NM", label: "New Mexico" },
    { value: "NY", label: "New York" },
    { value: "NC", label: "North Carolina" },
    { value: "ND", label: "North Dakota" },
    { value: "OH", label: "Ohio" },
    { value: "OK", label: "Oklahoma" },
    { value: "OR", label: "Oregon" },
    { value: "PA", label: "Pennsylvania" },
    { value: "RI", label: "Rhode Island" },
    { value: "SC", label: "South Carolina" },
    { value: "SD", label: "South Dakota" },
    { value: "TN", label: "Tennessee" },
    { value: "TX", label: "Texas" },
    { value: "UT", label: "Utah" },
    { value: "VT", label: "Vermont" },
    { value: "VA", label: "Virginia" },
    { value: "WA", label: "Washington" },
    { value: "WV", label: "West Virginia" },
    { value: "WI", label: "Wisconsin" },
    { value: "WY", label: "Wyoming" },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
    loadDoctors();
  }, []);

  const loadDoctors = async () => {
    try {
      setLoading(true);
      setError("");
      const token = getAuthToken();

      if (!token) {
        setError("Please log in to view your doctors");
        return;
      }

      const response = await getDoctors(token);

      if (response.success && response.data) {
        // Transform API data to match component structure
        // The data is directly an array, not nested under 'doctors'
        const doctorsData = Array.isArray(response.data)
          ? response.data
          : response.data.doctors || [];

        setDoctors(
          doctorsData.map((doctor) => ({
            _id: doctor._id || doctor.id,
            name: doctor.name,
            role: doctor.specialization,
            specialty: doctor.specialization,
            status: doctor.status,
            npi: doctor.npi || "",
            displayOnCard: doctor.displayOnCard,
          })),
        );
      }
    } catch (error) {
      console.error("Error loading doctors:", error);
      setError("Failed to load doctors. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Hide dropdown if clicked outside
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch from NPI API
  useEffect(() => {
    const fetchDoctors = async () => {
      if (!search || search.length < 2) {
        setApiResults([]);
        setIsDoctorLoading(false);
        return;
      }

      setIsDoctorLoading(true);
      try {
        // Build search query with location parameters
        let searchQuery = search;
        if (stateSearch) {
          searchQuery += ` ${stateSearch}`;
        }
        if (citySearch) {
          searchQuery += ` ${citySearch}`;
        }

        const url = `https://clinicaltables.nlm.nih.gov/api/npi_idv/v3/search?terms=${encodeURIComponent(
          searchQuery,
        )}&count=10&df=NPI,name.full,provider_type,gender,name.credential,addr_practice.full,addr_practice.phone,addr_practice.city,addr_practice.state`;
        const res = await fetch(url);
        const data = await res.json();
        const results = (data[3] || []).map((row, idx) => ({
          npi: data[1][idx],
          name: row[1] || "",
          specialty: row[2] || "",
          address: row[3] || "",
          phone: row[4] || "",
          city: row[5] || "",
          state: row[6] || "",
        }));

        // Filter results by state and city if specified
        let filteredResults = results;
        if (stateSearch) {
          filteredResults = filteredResults.filter(
            (doc) =>
              doc.state &&
              doc.state.toLowerCase().includes(stateSearch.toLowerCase()),
          );
        }
        if (citySearch) {
          filteredResults = filteredResults.filter(
            (doc) =>
              doc.city &&
              doc.city.toLowerCase().includes(citySearch.toLowerCase()),
          );
        }

        setApiResults(filteredResults);
        setShowDropdown(true);
      } catch (e) {
        console.error("Error fetching doctors:", e);
        setApiResults([]);
        setShowDropdown(true);
      } finally {
        setIsDoctorLoading(false);
      }
    };
    const timeout = setTimeout(fetchDoctors, 400);
    return () => clearTimeout(timeout);
  }, [search, stateSearch, citySearch]);

  // Dropdown options
  const serviceOptions = [
    { value: "cardiology", label: t("cardiology", lang) },
    { value: "neurology", label: t("neurology", lang) },
    { value: "orthopedics", label: t("orthopedics", lang) },
    { value: "pediatrics", label: t("pediatrics", lang) },
    { value: "general", label: t("generalPractice", lang) },
  ];
  const genderOptions = [
    { value: "", label: t("choose", lang) },
    { value: "male", label: t("male", lang) },
    { value: "female", label: t("female", lang) },
    { value: "other", label: t("other", lang) },
  ];

  // Handlers
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    if (apiResults.length >= 0) setShowDropdown(true);
    else setShowDropdown(false);
  };

  const handleStateChange = (e) => {
    setStateSearch(e.target.value);
  };

  const handleCityChange = (e) => {
    setCitySearch(e.target.value);
  };

  // Loading spinner component
  const LoadingSpinner = () => (
    <div className="flex items-center justify-center p-2">
      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#4962d8]"></div>
      <span className="ml-2 text-xs text-[#837f90]">Searching...</span>
    </div>
  );

  // No results component
  const NoResultsMessage = ({ searchTerm }) => (
    <div className="p-3 text-center">
      <div className="text-xs text-[#837f90] mb-1">
        No doctors found for "{searchTerm}"
      </div>
      <div className="text-xs text-[#837f90] opacity-75">
        Try a different search term or check spelling
      </div>
    </div>
  );

  // handleStatusChange removed since we no longer use status filtering
  const handleDoctorChange = (e) => {
    const { name, value } = e.target;
    setSelectedDoctor((prev) => ({ ...prev, [name]: value }));
  };

  const handleDoctorSelect = async (doctor) => {
    // Fetch full details for selected doctor
    try {
      const url = `https://clinicaltables.nlm.nih.gov/api/npi_idv/v3/search?terms=${doctor.npi}&count=1&df=NPI,name.full,provider_type,gender,name.credential,addr_practice.full,addr_practice.phone,addr_practice.fax,addr_practice.city,addr_practice.state&ef=licenses,addr_practice,addr_mailing,name,name_other,other_ids,misc`;
      const res = await fetch(url);
      const data = await res.json();
      const row = data[3] && data[3][0];

      setSelectedDoctor({
        name: row && row[1] ? row[1] : "",
        npi: doctor.npi,
        credential: row && row[4] ? row[4] : "",
        specialty: row && row[2] ? row[2] : "",
        service: row && row[2] ? row[2] : "",
        gender: row && row[3] ? row[3] : "",
        salId: "",
        address: row && row[5] ? row[5] : doctor.address,
        phone: row && row[6] ? row[6] : "",
        email: "",
        city: row && row[7] ? row[7] : doctor.city || "",
        state: row && row[8] ? row[8] : doctor.state || "",
      });
      setShowDropdown(false);
    } catch (e) {
      setShowDropdown(false);
    }
  };

  // Show add doctor modal
  const showAddDoctorModalHandler = (status = "current") => {
    if (!selectedDoctor.name) {
      alert("Please select a doctor first");
      return;
    }

    // Check if doctor already exists in the list by name
    const existingDoctor = doctors.find(
      (doc) => doc.name.toLowerCase() === selectedDoctor.name.toLowerCase(),
    );
    if (existingDoctor) {
      alert("This doctor is already in your list");
      return;
    }

    setDoctorToAdd({
      name: selectedDoctor.name,
      role: selectedDoctor.service || selectedDoctor.specialty,
      npi: selectedDoctor.npi,
      specialty: selectedDoctor.specialty,
    });
    setAddDoctorStatus(status);
    setShowAddDoctorModal(true);
  };

  // Add selected doctor to patient's list
  const addDoctorToList = async () => {
    if (!doctorToAdd) return;

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const token = getAuthToken();
      if (!token) {
        setError("Please log in to add doctors");
        return;
      }

      // Prepare data for API - map form fields to API schema
      const doctorData = {
        doctorName: doctorToAdd.name,
        role: doctorToAdd.role,
        specialtyDetails: doctorToAdd.specialty,
        status: addDoctorStatus,
      };

      const response = await addDoctor(doctorData, token);

      if (response.success) {
        setSuccess("Doctor added successfully!");
        // Reload doctors list
        await loadDoctors();

        // Clear the selected doctor form
        setSelectedDoctor({
          name: "",
          npi: "",
          credential: "",
          specialty: "",
          gender: "",
          salId: "",
          service: "",
          address: "",
          phone: "",
          email: "",
        });
        setSearch("");

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (error) {
      console.error("Error adding doctor:", error);
      setError(error.message || "Failed to add doctor. Please try again.");
    } finally {
      setLoading(false);
      // Close modal
      setShowAddDoctorModal(false);
      setDoctorToAdd(null);
    }
  };

  // Show delete confirmation modal
  const showDeleteConfirmation = (doctor) => {
    setDoctorToDelete(doctor);
    setShowDeleteModal(true);
  };

  // Remove doctor from list
  const removeDoctor = async () => {
    if (!doctorToDelete) return;

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const token = getAuthToken();
      if (!token) {
        setError("Please log in to remove doctors");
        return;
      }

      const response = await deleteDoctor(doctorToDelete._id, token);

      if (response.success) {
        setSuccess("Doctor removed successfully!");
        // Reload doctors list
        await loadDoctors();

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (error) {
      console.error("Error removing doctor:", error);
      setError(error.message || "Failed to remove doctor. Please try again.");
    } finally {
      setLoading(false);
      // Close modal
      setShowDeleteModal(false);
      setDoctorToDelete(null);
    }
  };

  // Edit doctor
  const editDoctor = (doctor) => {
    setDoctorToEdit(doctor);
    setEditDoctorData({
      name: doctor.name,
      role: doctor.role,
      specialty: doctor.specialty,
      status: doctor.status,
    });
    setShowEditDoctorModal(true);
  };

  // Save edited doctor
  const saveEditedDoctor = async () => {
    if (!doctorToEdit || !editDoctorData.name.trim()) return;

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const token = getAuthToken();
      if (!token) {
        setError("Please log in to update doctors");
        return;
      }

      // Prepare data for API - map form fields to API schema
      const doctorData = {
        doctorName: editDoctorData.name,
        role: editDoctorData.role,
        specialtyDetails: editDoctorData.specialty,
        status: editDoctorData.status,
      };

      const response = await updateDoctor(doctorToEdit._id, doctorData, token);

      if (response.success) {
        setSuccess("Doctor updated successfully!");
        // Reload doctors list
        await loadDoctors();

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (error) {
      console.error("Error updating doctor:", error);
      setError(error.message || "Failed to update doctor. Please try again.");
    } finally {
      setLoading(false);
      // Close modal
      setShowEditDoctorModal(false);
      setDoctorToEdit(null);
      setEditDoctorData({
        name: "",
        role: "",
        specialty: "",
        status: "current",
      });
    }
  };

  const showInfoColumn = doctors.some(
    (doctor) => doctor.displayOnCard === true,
  );

  return (
    <div className="min-h-screen bg-[#f7f8fa] flex flex-col items-center pb-8 px-4 md:px-0">
      <div className="w-full max-w-4xl">
        <div className="bg-white rounded-xl shadow p-8 mt-4">
          {/* Find a Doctor */}
          <AccountSection>{t("findDoctor", lang)}</AccountSection>

          {/* Error and Success Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                <span className="text-blue-700 font-medium">
                  Loading doctors...
                </span>
              </div>
            </div>
          )}

          {/* Search Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Doctor Name Search */}
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                Doctor Name
              </label>
              <div className="relative" ref={dropdownRef}>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={search}
                    onChange={handleSearchChange}
                    placeholder={t("findDoctorPlaceholder", lang)}
                    className="flex-1 border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                  />
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded bg-[#f7f8fa] border border-gray-200">
                    {isDoctorLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#4962d8]"></div>
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        fill="none"
                        stroke="#837f90"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <circle cx="11" cy="11" r="8" />
                        <path d="M21 21l-4.35-4.35" />
                      </svg>
                    )}
                  </span>
                </div>
                {showDropdown && (
                  <div className="absolute left-0 top-12 w-full bg-white border border-gray-200 rounded shadow-lg z-20 max-h-64 overflow-y-auto">
                    {isDoctorLoading ? (
                      <LoadingSpinner />
                    ) : apiResults.length > 0 ? (
                      apiResults.map((doc, idx) => (
                        <button
                          key={doc.npi + idx}
                          className="w-full text-left px-4 py-2 hover:bg-[#f7f8fa] border-b border-gray-100 last:border-b-0"
                          onClick={() => handleDoctorSelect(doc)}
                        >
                          <div className="font-semibold text-[#2d348f]">
                            {doc.name}
                          </div>
                          <div className="text-xs text-[#837f90]">
                            NPI: {doc.npi} &bull; {doc.specialty}
                          </div>
                          <div className="text-xs text-[#837f90]">
                            {doc.city && doc.state
                              ? `${doc.city}, ${doc.state}`
                              : doc.address}
                          </div>
                        </button>
                      ))
                    ) : search.length >= 2 ? (
                      <NoResultsMessage searchTerm={search} />
                    ) : null}
                  </div>
                )}
              </div>
            </div>

            {/* State Search */}
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                State
              </label>
              <select
                value={stateSearch}
                onChange={handleStateChange}
                className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
              >
                {usStates.map((state) => (
                  <option key={state.value} value={state.value}>
                    {state.label}
                  </option>
                ))}
              </select>
            </div>

            {/* City Search */}
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                City
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={citySearch}
                  onChange={handleCityChange}
                  placeholder="Enter city name"
                  className="flex-1 border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                />
                <span className="inline-flex items-center justify-center w-10 h-10 rounded bg-[#f7f8fa] border border-gray-200">
                  <svg
                    width="20"
                    height="20"
                    fill="none"
                    stroke="#837f90"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                  </svg>
                </span>
              </div>
            </div>
          </div>

          {/* Selected Doctor Form */}
          <AccountSection>{t("selectedDoctor", lang)}</AccountSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <AccountInput
              label={t("doctorName", lang)}
              name="name"
              value={selectedDoctor.name}
              onChange={handleDoctorChange}
            />
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("serviceProvided", lang)}
              </label>
              <select
                name="service"
                value={selectedDoctor.service}
                onChange={handleDoctorChange}
                className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
              >
                {serviceOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <AccountInput
              label={t("npiNumber", lang)}
              name="npi"
              value={selectedDoctor.npi}
              onChange={handleDoctorChange}
            />
            <AccountInput
              label={t("address", lang)}
              name="address"
              value={selectedDoctor.address}
              onChange={handleDoctorChange}
            />
            <AccountInput
              label="City"
              name="city"
              value={selectedDoctor.city}
              onChange={handleDoctorChange}
            />
            <AccountInput
              label="State"
              name="state"
              value={selectedDoctor.state}
              onChange={handleDoctorChange}
            />
            <AccountInput
              label={t("credential", lang)}
              name="credential"
              value={selectedDoctor.credential}
              onChange={handleDoctorChange}
            />
            <AccountInput
              label={t("phone", lang)}
              name="phone"
              value={selectedDoctor.phone}
              onChange={handleDoctorChange}
              isPhoneInput
            />
            <AccountInput
              label={t("specialty", lang)}
              name="specialty"
              value={selectedDoctor.specialty}
              onChange={handleDoctorChange}
            />
            <AccountInput
              label={t("email", lang)}
              name="email"
              value={selectedDoctor.email}
              onChange={handleDoctorChange}
            />
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("gender", lang)}
              </label>
              <select
                name="gender"
                value={selectedDoctor.gender}
                onChange={handleDoctorChange}
                className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
              >
                {genderOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <AccountInput
              label={t("salId", lang)}
              name="salId"
              value={selectedDoctor.salId}
              onChange={handleDoctorChange}
            />
          </div>

          {/* Add Doctor Buttons */}
          {selectedDoctor.name && (
            <div className="flex flex-col md:flex-row gap-4 mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-[#2d348f] mb-2">
                  Add Selected Doctor to Your List
                </h3>
                <p className="text-xs text-[#837f90] mb-3">
                  Dr. {selectedDoctor.name} - {selectedDoctor.specialty}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => showAddDoctorModalHandler("current")}
                  disabled={loading}
                  className={`px-4 py-2 text-white rounded text-sm transition-colors flex items-center gap-2 ${
                    loading
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-green-600 hover:bg-green-700"
                  }`}
                >
                  {loading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  Add as Main Doctor
                </button>
                <button
                  onClick={() => showAddDoctorModalHandler("past")}
                  disabled={loading}
                  className={`px-4 py-2 text-white rounded text-sm transition-colors flex items-center gap-2 ${
                    loading
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-gray-600 hover:bg-gray-700"
                  }`}
                >
                  {loading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  Add as Doctor{" "}
                  {doctors.filter((d) => d.status === "past").length + 1}
                </button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <AccountButton className="md:w-1/3" onClick={() => goToTab("next")}>
              <>
                {t("back", lang)} <span className="ml-2">&laquo;</span>
              </>
            </AccountButton>
            <AccountButton className="md:w-1/3" onClick={() => goToTab("prev")}>
              <span className="mr-2">&laquo;</span> {t("back", lang)}
            </AccountButton>
            <AccountButton className="md:w-1/3" onClick={() => goToTab("next")}>
              <>
                {t("next", lang)} <span className="ml-2">&raquo;</span>
              </>
            </AccountButton>
          </div>

          {/* My Doctors Table */}
          <AccountSection>{t("myDoctors", lang)}</AccountSection>

          {/* Current Doctors */}
          {doctors.filter((doc) => doc.status === "current").length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-[#2d348f] mb-3 flex items-center gap-2">
                <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                Main Doctor
              </h3>
              <div className="overflow-x-auto max-w-xs sm:max-w-full">
                <table className="bg-white text-sm min-w-full">
                  <thead>
                    <tr className="bg-green-50 text-[#837f90]">
                      {showInfoColumn && (
                        <th className="px-4 py-2 text-left font-semibold">
                          {t("Info", lang)}
                        </th>
                      )}
                      <th className="px-4 py-2 w-48 text-left font-semibold">
                        {t("doctorName", lang)}
                      </th>
                      <th className="px-4 py-2 text-left font-semibold">
                        {t("role", lang)}
                      </th>
                      <th className="px-4 py-2 text-left font-semibold">
                        {t("npiNumber", lang)}
                      </th>
                      <th className="px-4 py-2 text-left font-semibold">
                        {t("specialty", lang)}
                      </th>
                      <th className="px-4 py-2 text-left font-semibold">
                        {t("actions", lang)}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {doctors
                      .filter((doc) => doc.status === "current")
                      .map((doc, idx) => (
                        <tr key={doc.npi} className="border-t border-gray-100">
                          {showInfoColumn && (
                            <td className="px-4 py-2">
                              {doc.displayOnCard && <DisplayOnCardPill />}
                            </td>
                          )}
                          <td className="px-4 py-2 w-48 font-medium">
                            {replaceNullWithPlaceholder(doc.name)}
                          </td>
                          <td className="px-4 py-2">
                            {replaceNullWithPlaceholder(doc.role)}
                          </td>
                          <td className="px-4 py-2">
                            {replaceNullWithPlaceholder(doc.npi)}
                          </td>
                          <td className="px-4 py-2">
                            {replaceNullWithPlaceholder(doc.specialty)}
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              <span className="inline-block px-3 py-1 text-xs rounded-full bg-green-100 text-green-700">
                                Main
                              </span>
                              <button
                                onClick={() => editDoctor(doc)}
                                disabled={loading}
                                className={`px-3 py-1 text-sm rounded-lg transition-all duration-200 ${
                                  loading
                                    ? "text-gray-400 cursor-not-allowed border border-gray-400"
                                    : "text-blue-600 hover:text-blue-900 hover:bg-blue-50 border border-blue-600"
                                }`}
                                title={t("edit", lang)}
                              >
                                {t("edit", lang)}
                              </button>
                              <button
                                onClick={() => showDeleteConfirmation(doc)}
                                disabled={loading}
                                className={`p-2 rounded-lg transition-all duration-200 ${
                                  loading
                                    ? "text-gray-400 cursor-not-allowed"
                                    : "text-red-600 hover:text-red-900 hover:bg-red-50"
                                }`}
                                title="Remove Doctor"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Past Doctors */}
          {doctors.filter((doc) => doc.status === "past").length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-[#2d348f] mb-3 flex items-center gap-2">
                <span className="w-3 h-3 bg-gray-500 rounded-full"></span>
                Other Doctors
              </h3>
              <div className="overflow-x-auto max-w-xs sm:max-w-full">
                <table className="bg-white text-sm min-w-full">
                  <thead>
                    <tr className="bg-gray-50 text-[#837f90]">
                      {showInfoColumn && (
                        <th className="px-4 py-2 text-left font-semibold">
                          {t("Info", lang)}
                        </th>
                      )}
                      <th className="px-4 py-2 text-left font-semibold">
                        {t("doctorName", lang)}
                      </th>
                      <th className="px-4 py-2 text-left font-semibold">
                        {t("role", lang)}
                      </th>
                      <th className="px-4 py-2 text-left font-semibold">
                        {t("npiNumber", lang)}
                      </th>
                      <th className="px-4 py-2 text-left font-semibold">
                        {t("specialty", lang)}
                      </th>
                      <th className="px-4 py-2 text-left font-semibold">
                        {t("actions", lang)}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {doctors
                      .filter((doc) => doc.status === "past")
                      .map((doc, idx) => (
                        <tr
                          key={doc.npi}
                          className="border-t border-gray-100 overflow-x-auto"
                        >
                          {showInfoColumn && (
                            <td className="px-4 py-2 font-medium">
                              {doc.displayOnCard && <DisplayOnCardPill />}
                            </td>
                          )}
                          <td className="px-4 py-2 font-medium">
                            {replaceNullWithPlaceholder(doc.name)}
                          </td>
                          <td className="px-4 py-2">
                            {replaceNullWithPlaceholder(doc.role)}
                          </td>
                          <td className="px-4 py-2">
                            {replaceNullWithPlaceholder(doc.npi)}
                          </td>
                          <td className="px-4 py-2">
                            {replaceNullWithPlaceholder(doc.specialty)}
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              <span className="inline-block px-3 py-1 text-xs rounded-full bg-gray-100 text-gray-700">
                                {`Doctor ${idx + 1}`}
                              </span>
                              <button
                                onClick={() => editDoctor(doc)}
                                disabled={loading}
                                className={`px-3 py-1 text-sm rounded-lg transition-all duration-200 ${
                                  loading
                                    ? "text-gray-400 cursor-not-allowed border border-gray-400"
                                    : "text-blue-600 hover:text-blue-900 hover:bg-blue-50 border border-blue-600"
                                }`}
                                title={t("edit", lang)}
                              >
                                {t("edit", lang)}
                              </button>
                              <button
                                onClick={() => showDeleteConfirmation(doc)}
                                disabled={loading}
                                className={`p-2 rounded-lg transition-all duration-200 ${
                                  loading
                                    ? "text-gray-400 cursor-not-allowed"
                                    : "text-red-600 hover:text-red-900 hover:bg-red-50"
                                }`}
                                title="Remove Doctor"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* No Doctors Message */}
          {doctors.length === 0 && !loading && (
            <div className="text-center py-8 text-[#837f90]">
              <div className="text-lg mb-2">No doctors in your list yet</div>
              <div className="text-sm">
                Search for a doctor above and add them to your list
              </div>
            </div>
          )}

          {/* Loading State for Doctors List */}
          {loading && doctors.length === 0 && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4962d8] mx-auto mb-4"></div>
              <div className="text-[#837f90] font-medium">
                Loading your doctors...
              </div>
            </div>
          )}

          {/* Edit Doctor Modal */}
          {showEditDoctorModal && doctorToEdit && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                      <svg
                        className="w-6 h-6 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Edit Doctor Information
                    </h3>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Doctor Name
                      </label>
                      <input
                        type="text"
                        value={editDoctorData.name}
                        onChange={(e) =>
                          setEditDoctorData((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter doctor name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Role/Specialty
                      </label>
                      <input
                        type="text"
                        value={editDoctorData.role}
                        onChange={(e) =>
                          setEditDoctorData((prev) => ({
                            ...prev,
                            role: e.target.value,
                          }))
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter role or specialty"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Specialty Details
                      </label>
                      <input
                        type="text"
                        value={editDoctorData.specialty}
                        onChange={(e) =>
                          setEditDoctorData((prev) => ({
                            ...prev,
                            specialty: e.target.value,
                          }))
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter specialty details"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        value={editDoctorData.status}
                        onChange={(e) =>
                          setEditDoctorData((prev) => ({
                            ...prev,
                            status: e.target.value,
                          }))
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="current">Main Doctor</option>
                        <option value="past">
                          Doctor{" "}
                          {doctors.filter((d) => d.status === "past").length +
                            (doctorToEdit?.status === "past" ? 0 : 1)}
                        </option>
                      </select>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={() => {
                        setShowEditDoctorModal(false);
                        setDoctorToEdit(null);
                        setEditDoctorData({
                          name: "",
                          role: "",
                          specialty: "",
                          status: "current",
                        });
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveEditedDoctor}
                      disabled={loading}
                      className={`flex-1 px-4 py-2 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 ${
                        loading
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700"
                      }`}
                    >
                      {loading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      )}
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Add Doctor Confirmation Modal */}
          {showAddDoctorModal && doctorToAdd && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 ${
                        addDoctorStatus === "current"
                          ? "bg-green-100"
                          : "bg-gray-100"
                      }`}
                    >
                      <svg
                        className={`w-6 h-6 ${
                          addDoctorStatus === "current"
                            ? "text-green-600"
                            : "text-gray-600"
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Add{" "}
                      {addDoctorStatus === "current"
                        ? "Main"
                        : `Doctor ${
                            doctors.filter((d) => d.status === "past").length +
                            1
                          }`}
                    </h3>
                  </div>

                  <div className="mb-6">
                    <p className="text-gray-600 mb-3">
                      Are you sure you want to add this doctor as{" "}
                      {addDoctorStatus === "current"
                        ? "Main Doctor"
                        : `Doctor ${
                            doctors.filter((d) => d.status === "past").length +
                            1
                          }`}
                      ?
                    </p>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="font-semibold text-gray-900">
                        {doctorToAdd.name}
                      </div>
                      <div className="text-sm text-gray-600">
                        {doctorToAdd.specialty}
                      </div>
                      <div className="text-sm text-gray-500">
                        NPI: {doctorToAdd.npi}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={() => {
                        setShowAddDoctorModal(false);
                        setDoctorToAdd(null);
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={addDoctorToList}
                      className={`flex-1 px-4 py-2 text-white font-medium rounded-lg transition-colors ${
                        addDoctorStatus === "current"
                          ? "bg-green-600 hover:bg-green-700"
                          : "bg-gray-600 hover:bg-gray-700"
                      }`}
                    >
                      Add Doctor
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteModal && doctorToDelete && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                      <svg
                        className="w-6 h-6 text-red-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Remove Doctor
                    </h3>
                  </div>

                  <div className="mb-6">
                    <p className="text-gray-600 mb-3">
                      Are you sure you want to remove this doctor from your
                      list? This action cannot be undone.
                    </p>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="font-semibold text-gray-900">
                        {doctorToDelete.name}
                      </div>
                      <div className="text-sm text-gray-600">
                        {doctorToDelete.specialty}
                      </div>
                      <div className="text-sm text-gray-500">
                        NPI: {doctorToDelete.npi}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={() => {
                        setShowDeleteModal(false);
                        setDoctorToDelete(null);
                      }}
                      disabled={loading}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={removeDoctor}
                      disabled={loading}
                      className={`flex-1 px-4 py-2 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 ${
                        loading
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-red-600 hover:bg-red-700"
                      }`}
                    >
                      {loading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      )}
                      Remove Doctor
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
